import Dexie, { Table } from 'dexie';

export interface CachedChatMessage {
  cacheKey: string;    // `${loggedInUserId}::${conversationId}`
  server_id: number;   // chat_messages.id dari server
  conversation_id: number;
  sender_id: number;
  sender_name: string | null;
  body: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  reply_to: { id: number; sender_name: string | null; body: string | null; attachment_name: string | null; deleted: boolean; created_at: string } | null;
  edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
}

export interface CachedConversation {
  cacheKey: string;         // loggedInUserId — daftar percakapan milik user ini
  conversation_id: number;
  type: 'direct' | 'group';
  title: string;
  photo: string | null;
  participant_count: number;
  last_message: any;
  updated_at: string;
  is_unread: boolean;
}

interface ClearMarker {
  cacheKey: string; // `${loggedInUserId}::${conversationId}`
  cleared_at: string | null;
}

class ChatCacheDB extends Dexie {
  messages!: Table<CachedChatMessage, [string, number]>;
  conversations!: Table<CachedConversation, [string, number]>;
  clearMarkers!: Table<ClearMarker, string>;

  constructor() {
    // v2: nama DB baru (bukan bump version()) karena skema primary key berubah dari
    // auto-increment ke compound key — menghindari ambiguitas upgrade path Dexie untuk
    // fitur yang belum pernah rilis (aman untuk mulai fresh, ini cuma cache).
    super('teratask_chat_cache_v2');
    // Primary key kombinasi [cacheKey+id] — bulkPut jadi idempoten by design (re-save
    // baris yang sama akan menimpa, bukan menambah baris baru), sehingga aman terhadap
    // effect yang terpanggil lebih dari sekali (mis. React StrictMode di dev) tanpa perlu
    // pola delete-lalu-insert yang rawan race condition.
    this.version(1).stores({
      messages: '[cacheKey+server_id], cacheKey, created_at',
      conversations: '[cacheKey+conversation_id], cacheKey, updated_at',
      clearMarkers: 'cacheKey',
    });
  }
}

const db = new ChatCacheDB();

const messageKey = (loggedInUserId: string | number, conversationId: string | number) =>
  `${loggedInUserId}::${conversationId}`;

export const chatCache = {
  async getMessages(loggedInUserId: string | number, conversationId: string | number): Promise<CachedChatMessage[]> {
    const cacheKey = messageKey(loggedInUserId, conversationId);
    const rows = await db.messages.where('cacheKey').equals(cacheKey).toArray();
    return rows.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  },

  async saveMessages(loggedInUserId: string | number, conversationId: string | number, messages: any[]): Promise<void> {
    if (!messages.length) return;
    const cacheKey = messageKey(loggedInUserId, conversationId);
    await db.messages.bulkPut(messages.map((m) => ({
      cacheKey,
      server_id: m.id,
      conversation_id: m.conversation_id,
      sender_id: m.sender_id,
      sender_name: m.sender_name ?? null,
      body: m.body,
      attachment_url: m.attachment_url,
      attachment_name: m.attachment_name,
      attachment_type: m.attachment_type,
      reply_to: m.reply_to ?? null,
      edited_at: m.edited_at ?? null,
      deleted_at: m.deleted_at ?? null,
      created_at: m.created_at,
    })));
  },

  async getLastSyncedAt(loggedInUserId: string | number, conversationId: string | number): Promise<string | null> {
    const cached = await chatCache.getMessages(loggedInUserId, conversationId);
    if (!cached.length) return null;
    return cached[cached.length - 1].created_at;
  },

  /** Hapus semua pesan yang di-cache untuk 1 percakapan di device ini saja. */
  async clearMessagesForConversation(loggedInUserId: string | number, conversationId: string | number): Promise<void> {
    const cacheKey = messageKey(loggedInUserId, conversationId);
    await db.messages.where('cacheKey').equals(cacheKey).delete();
  },

  /**
   * Bandingkan cleared_at dari server terhadap yang terakhir diketahui device ini.
   * Kalau server punya cleared_at yang lebih baru (berarti ada "Clear Chat" terjadi di
   * device lain sejak device ini terakhir sync), hapus cache pesan lokal supaya tidak
   * menampilkan pesan yang seharusnya sudah di-clear. Selalu update marker ke nilai terbaru.
   * Return true kalau cache lokal barusan di-wipe (pemanggil perlu reset state pesan di memori juga).
   */
  async reconcileClearedAt(loggedInUserId: string | number, conversationId: string | number, serverClearedAt: string | null): Promise<boolean> {
    const cacheKey = messageKey(loggedInUserId, conversationId);
    const known = await db.clearMarkers.get(cacheKey);
    const knownAt = known?.cleared_at || null;

    const serverTime = serverClearedAt ? new Date(serverClearedAt).getTime() : 0;
    const knownTime = knownAt ? new Date(knownAt).getTime() : 0;

    let wiped = false;
    if (serverTime > knownTime) {
      await chatCache.clearMessagesForConversation(loggedInUserId, conversationId);
      wiped = true;
    }
    await db.clearMarkers.put({ cacheKey, cleared_at: serverClearedAt });
    return wiped;
  },

  async getConversationList(loggedInUserId: string | number): Promise<CachedConversation[]> {
    const rows = await db.conversations.where('cacheKey').equals(String(loggedInUserId)).toArray();
    return rows.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  },

  async saveConversationList(loggedInUserId: string | number, list: any[]): Promise<void> {
    const cacheKey = String(loggedInUserId);
    const freshIds = new Set(list.map((c) => c.id));

    // Hapus dari cache lokal percakapan yang sudah tidak ada di server (mis. keluar grup)
    const existing = await db.conversations.where('cacheKey').equals(cacheKey).toArray();
    const toRemove = existing.filter((c) => !freshIds.has(c.conversation_id));
    if (toRemove.length) {
      await db.conversations.bulkDelete(toRemove.map((c) => [c.cacheKey, c.conversation_id]));
    }

    if (!list.length) return;
    await db.conversations.bulkPut(list.map((c) => ({
      cacheKey,
      conversation_id: c.id,
      type: c.type,
      title: c.title,
      photo: c.photo,
      participant_count: c.participant_count,
      last_message: c.last_message,
      updated_at: c.updated_at,
      is_unread: c.is_unread,
    })));
  },
};
